/**
 * Phase 132 — Super Admin StreamX Overview (/admin/livex)
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import '@/styles/livex.css';
import {
  ArrowLeft, Radio, Trophy, Users, Receipt, TrendingUp,
  Building2, AlertTriangle, ChevronRight, Mail, BarChart3, MessageCircle,
} from 'lucide-react';
import { LivexShimmerKpi } from '@/components/livex/LivexShimmer';
import RecordingHealthBadge from '@/components/admin/RecordingHealthBadge';
import RefundWorkerStatusCard from '@/components/admin/RefundWorkerStatusCard';
import OperationsHealthCard from '@/components/admin/OperationsHealthCard';

function formatINR(rupees) {
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(rupees || 0);
}

function StatTile({ icon: Icon, value, label, accent, testid, href }) {
  const card = (
    <div className="p-5 bg-[#07111F] border-2 border-[#22314F] rounded-xl hover:border-[#FF5A1F] transition-all" data-testid={testid}>
      <Icon className="h-5 w-5 mb-2" style={{ color: accent || '#FF5A1F' }} />
      <div className="text-3xl font-black text-white tabular-nums leading-none">{value}</div>
      <div className="livex-label mt-2" style={{ marginBottom: 0 }}>{label}</div>
    </div>
  );
  return href ? <Link to={href}>{card}</Link> : card;
}

export default function LivexAdminOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    api.get('/livex/admin/overview')
      .then((r) => active && setData(r.data))
      .catch((e) => active && setError(e?.response?.data?.detail || 'Failed to load'));
    return () => { active = false; };
  }, []);

  if (error) {
    return <div className="min-h-screen grid place-items-center text-white px-6" style={{ background: '#0a0a0f' }}>
      <div className="p-5 bg-rose-950 border-2 border-rose-700 rounded-xl text-rose-100 font-bold flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
    </div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen text-white px-4 sm:px-6 py-6" style={{ background: '#0a0a0f' }} data-testid="livex-admin-overview-loading">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="h-10 w-48 livex-shimmer rounded" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Array.from({ length: 8 }).map((_, i) => <LivexShimmerKpi key={i} />)}
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-24 livex-shimmer rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="livex-admin-overview">
      <div className="border-b-2 border-[#22314F] bg-[#07111F]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate('/super-admin')} className="inline-flex items-center gap-2 text-sm font-bold text-white hover:text-[#FF5A1F] transition-colors">
            <ArrowLeft className="h-4 w-4" /> Super Admin
          </button>
          <div className="text-xs tracking-[0.3em] font-black text-[#FF5A1F]">STREAMX · ADMIN</div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-white">StreamX SaaS overview</h1>
            <p className="livex-body-text mt-2">Subscriptions, revenue, streams, engagement — at a glance.</p>
          </div>
          <RecordingHealthBadge />
        </div>

        {/* Revenue + subs row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatTile icon={TrendingUp} value={`₹${formatINR(data.revenue.mrr_inr)}`} label="MRR" accent="#22c55e" testid="livex-admin-mrr" />
          <StatTile icon={Receipt} value={`₹${formatINR(data.revenue.total_paid_inr)}`} label="Paid Revenue" accent="#22c55e" testid="livex-admin-revenue" />
          <StatTile icon={Users} value={data.subscriptions.pro_active} label="Pro Active" testid="livex-admin-pro-active" />
          <StatTile icon={Building2} value={data.academies.total} label="Total Academies" testid="livex-admin-academies-total" />
        </div>

        {/* Streams + engagement */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatTile icon={Radio} value={data.streams.live_now} label="Live Now" accent="#ef4444" testid="livex-admin-live-now" />
          <StatTile icon={Radio} value={data.streams.this_month} label="Streams This Month" testid="livex-admin-streams-month" />
          <StatTile icon={Trophy} value={data.engagement.total_highlights} label="Highlights" accent="#f59e0b" testid="livex-admin-highlights" />
          <StatTile icon={AlertTriangle} value={data.queue.pending_upgrades} label="Pending Upgrades" accent="#FF5A1F" testid="livex-admin-pending-upgrades" />
        </div>

        {/* Invoice queue */}
        <div className="grid sm:grid-cols-2 gap-3">
          <Link to="/admin/livex/invoices?status=draft,sent" data-testid="livex-admin-invoices-pending-link" className="block p-5 bg-[#07111F] border-2 border-[#22314F] rounded-xl hover:border-[#FF5A1F] transition-all flex items-center justify-between">
            <div>
              <div className="livex-label" style={{ marginBottom: '6px' }}>Pending Invoices</div>
              <div className="text-3xl font-black text-white tabular-nums">{data.revenue.invoices_pending}</div>
              <div className="text-xs font-bold text-[#94A3B8] mt-1">Generate or mark paid</div>
            </div>
            <ChevronRight className="h-5 w-5 text-[#FF5A1F]" />
          </Link>
          <Link to="/admin/livex/invoices?status=paid" data-testid="livex-admin-invoices-paid-link" className="block p-5 bg-[#07111F] border-2 border-[#22314F] rounded-xl hover:border-[#FF5A1F] transition-all flex items-center justify-between">
            <div>
              <div className="livex-label" style={{ marginBottom: '6px' }}>Paid Invoices</div>
              <div className="text-3xl font-black text-white tabular-nums">{data.revenue.invoices_paid}</div>
              <div className="text-xs font-bold text-[#94A3B8] mt-1">Revenue confirmed</div>
            </div>
            <ChevronRight className="h-5 w-5 text-[#FF5A1F]" />
          </Link>
        </div>

        {/* Quick links */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <Link to="/admin/livex/academies" data-testid="livex-admin-quick-academies" className="block p-5 bg-gradient-to-br from-[#1a0e10] to-[#07111F] border-2 border-[#3a1a1f] hover:border-[#FF5A1F] rounded-xl transition-all">
            <Building2 className="h-5 w-5 text-[#FF5A1F] mb-2" />
            <div className="text-base font-black text-white">Manage Academies</div>
            <div className="livex-body-text mt-1" style={{ fontSize: '12px' }}>Flip subscriptions · view usage</div>
          </Link>
          <Link to="/admin/livex/invoices" data-testid="livex-admin-quick-invoices" className="block p-5 bg-gradient-to-br from-[#1a0e10] to-[#07111F] border-2 border-[#3a1a1f] hover:border-[#FF5A1F] rounded-xl transition-all">
            <Receipt className="h-5 w-5 text-[#FF5A1F] mb-2" />
            <div className="text-base font-black text-white">Invoices</div>
            <div className="livex-body-text mt-1" style={{ fontSize: '12px' }}>Generate · download · mark paid</div>
          </Link>
          <Link to="/admin/livex/emails" data-testid="livex-admin-quick-emails" className="block p-5 bg-gradient-to-br from-[#1a0e10] to-[#07111F] border-2 border-[#3a1a1f] hover:border-[#FF5A1F] rounded-xl transition-all">
            <Mail className="h-5 w-5 text-[#FF5A1F] mb-2" />
            <div className="text-base font-black text-white">Email Console</div>
            <div className="livex-body-text mt-1" style={{ fontSize: '12px' }}>Lifecycle delivery + open rates</div>
          </Link>
          <Link to="/admin/whatsapp" data-testid="livex-admin-quick-whatsapp" className="block p-5 bg-gradient-to-br from-[#0e1a14] to-[#07111F] border-2 border-[#1a3a2a] hover:border-[#22c55e] rounded-xl transition-all">
            <MessageCircle className="h-5 w-5 text-[#22c55e] mb-2" />
            <div className="text-base font-black text-white">WhatsApp Console</div>
            <div className="livex-body-text mt-1" style={{ fontSize: '12px' }}>Templates · test send · audit log</div>
          </Link>
          <Link to="/admin/livex/cohorts" data-testid="livex-admin-quick-cohorts" className="block p-5 bg-gradient-to-br from-[#1a0e10] to-[#07111F] border-2 border-[#3a1a1f] hover:border-[#FF5A1F] rounded-xl transition-all">
            <BarChart3 className="h-5 w-5 text-[#FF5A1F] mb-2" />
            <div className="text-base font-black text-white">Cohort Funnel</div>
            <div className="livex-body-text mt-1" style={{ fontSize: '12px' }}>Activation · conversion · MRR</div>
          </Link>
          <Link to="/super-admin/livex-waitlist" data-testid="livex-admin-quick-waitlist" className="block p-5 bg-gradient-to-br from-[#1a0e10] to-[#07111F] border-2 border-[#3a1a1f] hover:border-[#FF5A1F] rounded-xl transition-all">
            <Users className="h-5 w-5 text-[#FF5A1F] mb-2" />
            <div className="text-base font-black text-white">Demand Waitlist</div>
            <div className="livex-body-text mt-1" style={{ fontSize: '12px' }}>Pre-Pro leads + signals</div>
          </Link>
        </div>

        {/* StreamX Sprint 3a — Refund worker operational visibility */}
        {/* StreamX Sprint 3b — Aggregated operations health */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <OperationsHealthCard />
          <RefundWorkerStatusCard />
        </div>
      </div>
    </div>
  );
}
